import { FC } from 'react'

import { Box, GlobalStyles, Paper, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'

import { useGetPriceChartsQuery } from 'services/dashboard-analytics/api'
import { DashboardAnalyticsFilter } from 'services/dashboard-analytics/types'
import { chartsTooltipClasses } from '@mui/x-charts'

interface PriceChartProps {
  filters: DashboardAnalyticsFilter[]
}

const PriceChart: FC<PriceChartProps> = ({ filters }) => {
  const { data } = useGetPriceChartsQuery({
    filters: filters,
  },
    {
      refetchOnMountOrArgChange: true,
    })

  const dates =
    data?.map((item) => {
      const date = new Date(item.date)
      return date.toLocaleDateString('en-CA')
    }) || []

  const avgPriceData =
    data?.map((item) => {
      return item.ticketsCnt > 0 ? item.totalSold / item.ticketsCnt : 0
    }) || []

  const chartDataWithMeta =
    data?.map((item) => ({
      minPrice: item?.minPrice || 0,
      maxPrice: item?.maxPrice || 0,
      ticketsCount: item?.ticketsCnt,
    })) || []

  return (
    <Paper
      sx={{
        p: '16px',
        py: '8px',
        boxShadow: '0px 2px 10px 2px rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        height: '500px',
        flex: 1,
        minWidth:'400px'
      }}
    >
      <GlobalStyles
        styles={{
          [`.${chartsTooltipClasses.valueCell}`]: { whiteSpace: 'pre-line' },
          [`.${chartsTooltipClasses.row}`]: { alignItems: 'flex-start' }
        }}
      />
      <Typography variant="h3">Price Chart</Typography>
      <Box sx={{ height: 'calc(100% - 60px)' }}>
        <LineChart
          xAxis={[
            {
              data: dates,
              scaleType: 'band',
              valueFormatter: (value) => value,
            },
          ]}
          series={[
            {
              data: avgPriceData,
              label: 'Price',
              color: '#2196f3',
              curve: 'linear',
              showMark: true,
              area: true,
              valueFormatter: (value) => {
                const index = avgPriceData?.indexOf(value as number)
                const meta = chartDataWithMeta?.[index || 0]
                if (!meta) return `$${(value || 0).toFixed(2)}`

                return [
                  `Min: €${meta.minPrice}`,
                  `Max: €${meta.maxPrice}`,
                  `Tickets: ${meta.ticketsCount}`,
                ].join('\n')
              },
            },
          ]}
          slotProps={{
            legend: {
              direction: 'row',
              position: { vertical: 'top', horizontal: 'left' },
              itemMarkWidth: 0,
              itemMarkHeight: 0,
              markGap: 0,
              itemGap: 10,
            },
          }}
          tooltip={{
            trigger: 'axis',
          }}
        />
      </Box>
    </Paper>
  )
}

export default PriceChart
